import * as React from 'react';
import type { CalendarEvent } from '../../../MedicareProAPI/Entities/CalendarEvent';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import { DAYS } from '../../../MedicareProAPI/Utilities/Languages';
import { UserAvatar } from '../User/UserAvatar';
import type { User } from '../../../MedicareProAPI/Entities/User/User';

export interface CalendarMonthProps {
  events?: CalendarEvent[];
  dateTime?: Date;
  showDownlines?: boolean;
  onEdit?: (calendarEvent: CalendarEvent) => void;
  showWeekend?: boolean;
  users?: User[];
}

export interface CalendarMonthDay {
  events: CalendarEvent[];
  dateTime: Date;
}

export const CalendarMonth = (props: CalendarMonthProps): React.JSX.Element => {
  const [dateTimeStart, setDateTimeStart] = React.useState<Date>();
  const [dateTimeEnd, setDateTimeEnd] = React.useState<Date>();
  const [events, setEvents] = React.useState<CalendarEvent[]>();
  const [days, setDays] = React.useState<CalendarMonthDay[]>();
  const [users, setUsers] = React.useState<User[]>();
  const [showDownlines, setShowDownlines] = React.useState<boolean>(false);
  const [showWeekend, setShowWeekend] = React.useState<boolean>(true);

  const date = new Date();
  const today = date.getDate();

  React.useEffect(() => {
    if (props.dateTime) {
      const firstDayOfMonth = Utilities.getFirstDayOfMonth(props.dateTime);
      const lastDayOfMonth = Utilities.getLastDayOfMonth(props.dateTime);

      const dateTimeStart = Utilities.getFirstDayOfWeek(firstDayOfMonth);
      const dateTimeEnd = Utilities.getLastDayOfWeek(lastDayOfMonth);

      setDateTimeStart(dateTimeStart);
      setDateTimeEnd(dateTimeEnd);
    }
  }, [props.dateTime]);

  React.useEffect(() => {
    if (props.events) {
      setEvents(props.events);
    }
  }, [props.events]);

  React.useEffect(() => {
    if (props.users) {
      setUsers(props.users);
    }
  }, [props.users]);

  React.useEffect(() => {
    setShowDownlines(props.showDownlines);
  }, [props.showDownlines]);

  React.useEffect(() => {
    if (dateTimeStart && dateTimeEnd) {
      const dateCursor = new Date(dateTimeStart);
      const dayList: CalendarMonthDay[] = [];

      while (dateCursor < dateTimeEnd) {
        const currentDay = new Date(dateCursor);
        dayList.push({
          events: [],
          dateTime: currentDay,
        });

        dateCursor.setDate(dateCursor.getDate() + 1);
      }

      if (events) {
        events.forEach((eventSingle) => {
          const eventDateKey = eventSingle.getDateTimeStart().toLocaleDateString();
          const eventDay = dayList.find((daySingle) => {
            const dayKey = daySingle.dateTime.toLocaleDateString();
            return eventDateKey === dayKey;
          });

          if (eventDay) {
            eventDay.events.push(eventSingle);
          }
        });
      }

      setDays(dayList);
    }
  }, [dateTimeStart, dateTimeEnd, events]);

  React.useEffect(() => {
    setShowWeekend(props.showWeekend);
  }, [props.showWeekend]);

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const containerClassNames = [
    'calendar-month-container',
    showDownlines ? 'show-downlines' : 'hide-downlines',
    showWeekend ? 'show-weekend' : 'hide-weekend',
  ];

  return (
    <div className={containerClassNames.join(' ')}>
      <div className="week-names">
        {DAYS.map((day, dayIndex) => (
          <div className={`day text-center day-${dayIndex}`} key={dayIndex}>
            <div className="pt-2 pb-1 " style={{ fontSize: '11pt' }}>
              {day}
            </div>
          </div>
        ))}
      </div>

      {days &&
        Utilities.chunk(days, 7).map((dayChunk, dayChunkIndex) => {
          return (
            <div className="month-events" key={dayChunkIndex}>
              {dayChunk.map((day, dayIndex) => {
                const highlight = isToday(day.dateTime) ? 'font-weight-bold text-success' : '';

                return (
                  <div className={`day p-2 day-${dayIndex}`} key={dayIndex}>
                    <div className={highlight}>{day.dateTime.getDate()}</div>
                    {day.events &&
                      day.events.map((eventSingle) => {
                        return (
                          <div key={eventSingle.getId()}>
                            {showDownlines === true && (
                              <UserAvatar
                                user={eventSingle.getUser()}
                                className="float-left mr-2"
                              />
                            )}
                            <div
                              className="badge badge-sm border badge-primary border-primary mb-1 text-left"
                              onClick={(e) => props.onEdit(eventSingle)}
                            >
                              {eventSingle.getName()}
                              <br />
                              <span className="font-weight-normal">
                                {eventSingle.isAllDayEvent()
                                  ? 'All day'
                                  : Utilities.dateTimeTo12HourTime(eventSingle.getDateTimeStart()) +
                                    ' - ' +
                                    Utilities.dateTimeTo12HourTime(eventSingle.getDateTimeEnd())}
                              </span>
                              {eventSingle.getInds() && eventSingle.getInds().length > 0 && (
                                <>
                                  <br />
                                  <em className="font-weight-normal">
                                    <>with </>
                                    {eventSingle.getInds().map((ind, index) => {
                                      return (
                                        ind.getIndName() +
                                        (eventSingle.getInds().length > 1 &&
                                        index + 1 !== eventSingle.getInds().length
                                          ? ', '
                                          : '')
                                      );
                                    })}
                                  </em>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          );
        })}
    </div>
  );
};
